import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FiArrowLeft, FiMail, FiTrash2, FiUser, FiClock, FiCheck, FiX, FiGlobe, FiUsers, FiEdit2, FiDownload, FiAlertCircle } from 'react-icons/fi';
import { IGlobalState } from '../../../../model/IGlobalState';
import { ContactListSummary } from '../../../../model/List';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import { toast } from '../../Toast';
import { ContactRow, useContactsData, listName, initials, splitName, contactKey as keyOf } from './contactsData';

const preview = (what: string) => toast.info(`Preview only: ${what} isn’t connected yet`);

const decode = (key: string) => {
    try { return decodeURIComponent(key).toLowerCase(); } catch { return key.toLowerCase(); }
};

type Kind = 'all' | 'emails' | 'visits' | 'questions' | 'lists';
interface ActivityEvent { kind: Kind; title: string; sub: string; when?: string }

// Communications › Contacts › a contact: Details (spec 588:4661) and Activity (spec 588:5308). Reads the real contact;
// edits, unsubscribe and delete are preview only. Website Hub account, share register and activity have no source yet.
const ContactDetail: React.FC<{ contactKey: string; view?: string }> = ({ contactKey, view }) => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const { data, error } = useContactsData(stock?.id);
    const wanted = decode(contactKey);
    const contact = data?.contacts.find(c => c.email.toLowerCase() === wanted);

    const back = <button type="button" className="link contacts-back" onClick={() => history.push('/communications/contacts')}><FiArrowLeft /> Back to Contacts</button>;
    if (error) return <div className="contacts-detail">{back}<p className="error">{error}</p></div>;
    if (!data) return <div className="contacts-detail">{back}<p className="loading">Loading contact…</p></div>;
    if (!contact) return <div className="contacts-detail">{back}<p className="contacts-missing">Contact not found — {wanted} isn’t in this client’s contacts.</p></div>;

    const base = `/communications/contacts/${keyOf(contact.email)}`;
    const activity = view === 'activity';
    const name = contact.name || contact.email;

    const navItem = (icon: React.ReactNode, title: string, sub: string, active: boolean, to: string) =>
        <button type="button" className={`contacts-nav-item${active ? ' active' : ''}`} onClick={() => !active && history.push(to)}>
            <span className="contacts-nav-icon">{icon}</span>
            <span className="contacts-nav-t"><strong>{title}</strong><span>{sub}</span></span>
        </button>;

    return <div className="contacts-detail">
        {back}
        <div className="contacts-title-row">
            <span className="contacts-avatar">{initials(contact.name, contact.email)}</span>
            <div className="contacts-title">
                <h1>{name}</h1>
                <div className="contacts-meta">
                    <span className={`pill ${contact.subscribed ? 'ok' : 'bad'}`}>{contact.subscribed ? 'Subscribed' : 'Unsubscribed'}</span>
                    {contact.tags.map(t => <span key={t} className="pill contacts-tag-pill">{t}</span>)}
                    <span>{contact.email} · added {moment(contact.created).format('D MMM YYYY')}{contact.consentSource ? ` · ${contact.consentSource}` : ''}</span>
                </div>
            </div>
            <div className="contacts-actions">
                {activity && <button type="button" className="btn-outline" onClick={() => history.push(base)}><FiEdit2 /> Edit Contact</button>}
                {contact.subscribed && <button type="button" className="btn-outline" onClick={() => preview('unsubscribing')}><FiMail /> Unsubscribe</button>}
                <button type="button" className="btn-outline contacts-danger" onClick={() => preview('deleting a contact')}><FiTrash2 /> Delete</button>
            </div>
        </div>

        <div className="contacts-layout">
            <nav className="card contacts-side-nav" aria-label="This contact">
                <div className="contacts-caps">This contact</div>
                {navItem(<FiUser />, 'Contact Details', 'Who they are and what they get', !activity, base)}
                {navItem(<FiClock />, 'Contact Activity', 'Emails, visits and questions', activity, `${base}/activity`)}
                <div className="contacts-side-permission">
                    <div className="contacts-caps">Permission</div>
                    {contact.subscribed
                        ? <div className="contacts-perm-status ok"><FiCheck /> Subscribed</div>
                        : <div className="contacts-perm-status bad"><FiX /> Unsubscribed</div>}
                    <div className="contacts-perm-sub">{contact.subscribed ? contact.consentSource || 'How they agreed isn’t recorded' : contact.unsubscribeReason || 'No reason given'}</div>
                </div>
            </nav>

            {activity
                ? <ContactActivity contact={contact} />
                : <ContactDetails key={contact.email} contact={contact} lists={data.lists} />}
        </div>
    </div>;
};

const CardHead: React.FC<{ icon: React.ReactNode; title: string; sub: string }> = ({ icon, title, sub }) =>
    <div className="contacts-card-head">
        <span className="icon-tile">{icon}</span>
        <div><h2>{title}</h2><p>{sub}</p></div>
    </div>;

const ContactDetails: React.FC<{ contact: ContactRow; lists: ContactListSummary[] }> = ({ contact, lists }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const site = (stock?.websiteUrl || '').replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    const initial = useMemo(() => {
        const n = splitName(contact.name);
        return { first: n.first, last: n.last, email: contact.email, phone: '', company: '', tags: contact.tags, lists: contact.listIds };
    }, [contact]);
    const [form, setForm] = useState(initial);
    const [tagText, setTagText] = useState('');
    useEffect(() => setForm(initial), [initial]);

    const same = (a: string[], b: string[]) => a.length === b.length && a.every(x => b.includes(x));
    const dirty = form.first !== initial.first || form.last !== initial.last || form.email !== initial.email || form.phone !== initial.phone
        || form.company !== initial.company || !same(form.tags, initial.tags) || !same(form.lists, initial.lists);

    const set = (k: 'first' | 'last' | 'email' | 'phone' | 'company') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setForm(f => ({ ...f, [k]: v }));
    };
    const addTag = () => {
        const t = tagText.trim().replace(/,$/, '');
        if (t && !form.tags.some(x => x.toLowerCase() === t.toLowerCase())) setForm(f => ({ ...f, tags: [...f.tags, t] }));
        setTagText('');
    };
    const tagKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
        else if (e.key === 'Backspace' && !tagText && form.tags.length) setForm(f => ({ ...f, tags: f.tags.slice(0, -1) }));
    };
    const toggleList = (id: string) => setForm(f => ({ ...f, lists: f.lists.includes(id) ? f.lists.filter(x => x !== id) : [...f.lists, id] }));

    const field = (label: string, value: string, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void, hint?: string, placeholder?: string) =>
        <label className="contacts-input">
            <span>{label}{hint && <em>{hint}</em>}</span>
            <input value={value} placeholder={placeholder} onChange={onChange} />
        </label>;

    const row = (label: string, value: React.ReactNode, strong = false) =>
        <div className="contacts-dl-row"><dt>{label}</dt><dd className={strong ? 'strong' : ''}>{value}</dd></div>;
    const notRecorded = <span className="contacts-muted">— not recorded</span>;

    return <div className="contacts-main-col">
        <div className="card">
            <CardHead icon={<FiUser />} title="Contact" sub="Edit anything here and press Save — no need to open a separate form" />
            <div className="contacts-form">
                <div className="contacts-pair">
                    {field('First name', form.first, set('first'))}
                    {field('Last name', form.last, set('last'))}
                </div>
                {field('Email address', form.email, set('email'), 'used for every campaign')}
                <div className="contacts-pair">
                    {field('Phone', form.phone, set('phone'), 'optional', 'Not recorded')}
                    {field('Company', form.company, set('company'), 'optional', 'Not recorded')}
                </div>
                <label className="contacts-input">
                    <span>Tags</span>
                    <span className="contacts-tag-input">
                        {form.tags.map(t => <span key={t} className="contacts-chip">{t}
                            <button type="button" aria-label={`Remove ${t}`} onClick={e => { e.preventDefault(); setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) })); }}><FiX /></button>
                        </span>)}
                        <input value={tagText} placeholder="Add a tag…" onChange={e => setTagText(e.target.value)} onKeyDown={tagKey} onBlur={addTag} />
                    </span>
                </label>
                <p className="contacts-note"><FiClock /> Added {moment(contact.created).format('D MMMM YYYY')}{contact.consentSource ? ` · ${contact.consentSource}` : ''}</p>
            </div>
        </div>

        <div className="card">
            <CardHead icon={<FiMail />} title="Lists and What They Receive" sub="Change any of these and it applies from the next send" />
            <div className="contacts-checks">
                {lists.map(l => {
                    const on = form.lists.includes(l.id);
                    return <label key={l.id} className={`contacts-check-row${on ? ' on' : ''}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleList(l.id)} />
                        <span>{listName(l)}</span>
                    </label>;
                })}
            </div>
        </div>

        <div className="card">
            <CardHead icon={<ShieldCheckIcon />} title="Permission" sub="How and when they agreed — kept as the record if anyone asks" />
            <dl className="contacts-dl">
                {row('Status', contact.subscribed ? 'Subscribed' : 'Unsubscribed', true)}
                {!contact.subscribed && row('Reason', contact.unsubscribeReason || '—')}
                {row('Agreed', notRecorded)}
                {row('How', contact.consentSource || '—')}
                {row('Recorded by', notRecorded)}
                {row('IP address', notRecorded)}
            </dl>
        </div>

        <div className="card">
            <CardHead icon={<FiGlobe />} title="Website Hub Account" sub={`Whether they have signed up on ${site || 'the website'} to read documents and ask questions`} />
            <p className="contacts-not-connected"><FiAlertCircle /> Not connected yet — Website Hub accounts aren’t linked to contacts</p>
        </div>

        <div className="card">
            <CardHead icon={<FiUsers />} title="On the Share Register" sub="The holder this contact is matched to on the register" />
            <p className="contacts-not-connected"><FiAlertCircle /> Not connected yet — contacts aren’t matched to the share register</p>
        </div>

        <div className="card contacts-save-bar">
            <span>Changes to the details, lists and tags are saved together.</span>
            <button type="button" className="btn-outline" disabled={!dirty} onClick={() => { setForm(initial); setTagText(''); }}>Cancel</button>
            <button type="button" className="btn-primary" disabled={!dirty} onClick={() => toast.info(`Preview only: changes to ${contact.email} weren’t saved`)}>Save Changes</button>
        </div>
    </div>;
};

const KINDS: { kind: Kind; label: string }[] = [
    { kind: 'all', label: 'Everything' },
    { kind: 'emails', label: 'Emails' },
    { kind: 'visits', label: 'Website Visits' },
    { kind: 'questions', label: 'Questions' },
    { kind: 'lists', label: 'List Changes' },
];

// only what the contact record itself proves: when they were added, and that they unsubscribed (no date is kept for that)
const ContactActivity: React.FC<{ contact: ContactRow }> = ({ contact }) => {
    const [kind, setKind] = useState<Kind>('all');
    const events: ActivityEvent[] = [];
    if (contact.unsubscribeReason || !contact.subscribed) {
        events.push({ kind: 'lists', title: 'Unsubscribed', sub: contact.unsubscribeReason ? `reason: ${contact.unsubscribeReason}` : 'no reason given' });
    }
    events.push({ kind: 'lists', title: 'Added to Contacts', sub: `${moment(contact.created).format('h:mm a')}${contact.consentSource ? ` · ${contact.consentSource}` : ''}`, when: contact.created });
    const shown = events.filter(e => kind === 'all' || e.kind === kind);
    const count = (k: Kind) => k === 'all' || k === 'lists' ? String(events.length) : '—';

    const groupOf = (e: ActivityEvent) => e.when ? moment(e.when).format('D MMMM YYYY') : 'Date not recorded';
    const stat = (label: string) =>
        <div className="contacts-stat"><div className="v">—</div><div className="l">{label}</div><div className="d">not recorded yet</div></div>;

    return <div className="contacts-main-col">
        <div className="contacts-stats">
            {stat('emails received')}
            {stat('links clicked')}
            {stat('website visits')}
            {stat('questions asked')}
        </div>

        <div className="contacts-kinds">
            {KINDS.map(k => <button key={k.kind} type="button" className={k.kind === kind ? 'active' : ''} onClick={() => setKind(k.kind)}>
                {k.label} <span className="n">{count(k.kind)}</span>
            </button>)}
            <span className="contacts-range">All time</span>
        </div>

        <div className="card">
            <CardHead icon={<FiClock />} title="Activity" sub="Everything this contact has done, newest first" />
            {kind !== 'all' && kind !== 'lists'
                ? <p className="contacts-empty">Activity isn’t recorded for contacts yet</p>
                : <div className="contacts-events">
                    {shown.map((e, i) => <React.Fragment key={e.title}>
                        {(i === 0 || groupOf(shown[i - 1]) !== groupOf(e)) && <div className="contacts-caps contacts-day">{groupOf(e)}</div>}
                        <div className="contacts-event"><strong>{e.title}</strong><span>{e.sub}</span></div>
                    </React.Fragment>)}
                    <p className="contacts-empty">Emails, website visits and questions aren’t recorded for contacts yet</p>
                </div>}
            <div className="contacts-events-foot">
                <span>{events.length} event{events.length === 1 ? '' : 's'} since {moment(contact.created).format('D MMMM YYYY')}</span>
                <button type="button" className="btn-outline" onClick={() => preview('exporting activity')}><FiDownload /> Export Activity</button>
            </div>
        </div>
    </div>;
};

export default ContactDetail;
